import React, { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import '@/pages/benchmark/assets/scss/page/explore/components/SearchComm.scss';
import SearchResult from './SearchResult';
import { AutoComplete, Input, Button, SelectOptionWithGroup, StatusTip, Icon } from 'tea-component';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { ISearchComm } from '@/pages/benchmark/types/explore';
import { FRAME_TYPE_MAP } from '@/pages/benchmark/_config/constant';
import { getMetricSearch } from '@/pages/benchmark/services/benchmark';
import { debounce } from 'lodash-es';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';

export default function SearchComm(props: ISearchComm) {
  const { onItemClick, placeholder, overlayClassName, className, onSearchClick, frameType } = props;
  const initialSearch = new URLSearchParams(window.location.search).get('search') || '';
  const [inputValue, setInputValue] = useState(initialSearch);
  const [inputError, setInputError] = useState('');
  const [isFocus, setIsFocus] = useState(false);
  const [searchResult, setSearchResult] = useState<SelectOptionWithGroup[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const { formatMessage, locale} = useIntl(); // 中英切换方法
  const isEN = locale === LANGUAGE_EN;

  const onTextChange = (value: string) => {
    setInputValue(value);
    setSearchLoading(true);
    const MAX_LENGTH = 50;
    const validCharactersRegex = /^[A-Za-z\s&()（）%$/-\d\u4e00-\u9fff]*$/;

    if(value.length > MAX_LENGTH) {
      setInputError(()=> formatMessage({id: 'dbb_explore_search_input_error_length'}));
    }else if(!validCharactersRegex.test(value)){
      setInputError(formatMessage({id: 'dbb_explore_search_input_error_special_char'}));
    }else {
      setInputError('');
    }
  };  

  useEffect(() => {
    setInputValue(initialSearch);
  }, [initialSearch]);
  
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
      setIsFocus(false);
      if(!inputError){
        onSearchClick?.(inputValue);
      }
    }
  };

  const fetchSearchResults = useCallback(async () => {
    setSearchResult([]);
    // setSearchLoading(true);
    setSearchError(false);
    
    const payload = { 
      keyword: inputValue,
      frame_type: FRAME_TYPE_MAP[frameType || 'pcconsole']
    };

    try {
      const res = await getMetricSearch(payload);
      const { data } = res;
      const result: SelectOptionWithGroup[] = (data?.list || []).map((val: { metric: string; metric_cn: string; metric_en: string; category: string; category_en: string; category_cn: string; icon: string; is_top: boolean;is_new:boolean; click_num: string; }) => {
        const { metric_cn: metricCN, metric_en: metricEN, category_en: categoryEn, category_cn: categoryCn, icon, is_top: isTop, is_new: isNew, click_num: clickNum } = val;
        const views = dataFormat({value: clickNum, formatRule: parseFloat(clickNum) > 1000 ? 'bmk0' : 'int'});
        const title = highlightText(isEN ? metricEN : metricCN, inputValue);
        const type = isEN ? categoryEn : categoryCn;
        return {
          value: isEN ? metricEN : metricCN,
          text: <SearchResult title={title} type={type} icon={icon} isHot={isTop} isNew={isNew} views={views}/>
        };
      });
      setSearchResult(result);
    } catch (e) {
      console.log('err', e);
      setSearchError(true);
    } finally {
      setSearchLoading(false);
    }
  }, [frameType, inputValue, isEN]);

  const highlightText = (text: string, search: string) => {
    if (!search) return text;
    const escapedSearch = search.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearch})`, 'gi'); // Case-insensitive regex
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} style={{ color: "#326ff0" }}>{part}</span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  const debounceFetchSearchResults = useMemo(()=>debounce(fetchSearchResults, 300), [fetchSearchResults]);

  useEffect(() => {
    if (inputError || !inputValue) {
      setSearchResult([]);
      setSearchError(false);
      setSearchLoading(false);
      return;
    }

    debounceFetchSearchResults();
    return () => {
      debounceFetchSearchResults.cancel(); // Clean up any pending debounce calls
    };
  }, [inputValue, inputError, debounceFetchSearchResults]);

  useEffect(()=> {
    setInputValue('');
    setInputError('');
  },[frameType]);

  useEffect(()=> {
    if(inputValue){
      onTextChange(inputValue);
    }
  },[inputValue, isEN]);

  return (
    // 报错 --error 
    <div className={classnames(inputError === '' ? 'dbb-searchcomm' : 'dbb-searchcomm --error', className)}>
      <AutoComplete
        open={isFocus && inputValue.length > 0 && !inputError}
        options={searchResult || []}
        keyword={inputValue}
        tips={            
          <StatusTip
            status={ searchLoading ? "loading" : searchError ? "error" : "empty"}
            loadingText={formatMessage({id: 'dbb_explore_search_loading'})}
            errorText={formatMessage({id: 'dbb_explore_search_fail'})}
            emptyText={formatMessage({id: 'dbb_explore_search_no_match'})}
          />
        }
        onChange={(value, context) => {
          // 避免点击enter键时走到这逻辑
          const event = context.event as React.KeyboardEvent<HTMLInputElement>;
          if (event && event.key === 'Enter') {
            return;
          }
          onTextChange(value);
          onItemClick?.(value);
        }}
        // onScrollBottom={(event) => console.log(event)}
        overlayClassName={classnames('dbb-searchcomm-overlay', overlayClassName)}
      >
        {(ref) => (
          <div className="dbb-searchcomm-hd">
            <Input
              ref={ref}
              value={inputValue}
              onFocus={()=> {
                setIsFocus(true);
              }}
              onBlur={()=> {
                setIsFocus(false);
              }}
              onChange={(value) => onTextChange(value)}
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
            />
            {
              inputValue.length > 0 && (
                <span onClick={()=>{ 
                  onTextChange('');
                  onSearchClick?.(''); 
                }} className='dbb-searchcomm-dismiss'>
                  <Icon type={'dismiss'}/>
                </span>
              )
            }
            <Button type="primary" onClick={()=>{ onSearchClick?.(inputValue);setIsFocus(false) }} disabled={inputError !== ''}>{formatMessage({id: 'dbb_explore_search'})}</Button>
          </div>
        )}
      </AutoComplete>
      <div className="dbb-searchcomm-err-text">
        {inputError}
      </div>
    </div>
  );
}
